import React, { useEffect, useMemo, useState, useRef } from "react";
import moment from "moment";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { RenderOpportunityStatus } from "../../../utils/CommonStatusItems";
import { RenderLearningMethodGradebook, RenderSchool } from "../../../utils/CommonGroupingItem";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import { TABLE_DATE_FORMAT } from "../../../utils/Constants";
import { renderBrand, renderCampus } from "../CallDashboardMappings";

const OpportunityBlock = ({ loading, opportunityData, schoolMapping = {}, brandMapping = {}, campusMapping = {}, error = null, onRetry = null }) => {
    const [search, setSearch] = useState("");
    const [dataFiltered, setDataFiltered] = useState([]);

    // Wrapper functions to pass brandMapping and campusMapping to the consolidated render functions
    const renderBrandWithMapping = (brandId) => renderBrand(brandId, brandMapping);
    const renderCampusWithMapping = (campusId) => renderCampus(campusId, campusMapping);

    const resolveSchoolSlug = (school) => {
        if (school === undefined || school === null || school === "") return "";
        const key = String(school);
        return schoolMapping?.[key] || key;
    };

    // Helper: stringify all row values for search-all-fields
    const rowToSearchText = (row) => {
        if (!row || typeof row !== 'object') return '';
        return Object.values(row)
            .map((val) => {
                if (val === null || val === undefined) return '';
                if (typeof val === 'object') {
                    try { return JSON.stringify(val); } catch { return ''; }
                }
                return String(val);
            })
            .join(' ')
            .toLowerCase();
    };
    
    useEffect(() => {
        if (search === "" || search === null) {
            setDataFiltered(opportunityData)
        } else {
            const term = search.trim().toLowerCase();
            setDataFiltered(opportunityData.filter((item) => rowToSearchText(item).includes(term)));
        }
    }, [search, opportunityData])

    // Format date to portal standard (DD MMM YY) - no time display for date-only values
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        // NetSuite dates come as "DD/MM/YYYY" or "MM/DD/YYYY" - try multiple formats
        const formats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'];
        const m = moment(dateStr, formats, true);
        if (!m.isValid()) return dateStr; // Fallback to original if unparseable
        return m.format(TABLE_DATE_FORMAT);
    };

    // Component for Qualification column with expandable text
    const QualificationCell = ({ qualification }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [showToggle, setShowToggle] = useState(false);
        const textRef = useRef(null);

        useEffect(() => {
            if (qualification && qualification !== "-" && textRef.current && !isExpanded) {
                // Only check truncation when collapsed to determine if toggle should be shown
                // When expanded, we keep showToggle as-is so "See Less" button remains visible
                const timeoutId = setTimeout(() => {
                    if (!textRef.current || isExpanded) return;
                    
                    const element = textRef.current;
                    
                    // Temporarily remove line-clamp to measure full height
                    const originalClasses = element.className;
                    element.classList.remove('line-clamp-3');
                    const fullHeight = element.scrollHeight;
                    element.className = originalClasses; // Restore original classes
                    
                    // After restoring, get the clamped height
                    const clampedHeight = element.clientHeight;
                    
                    // Check if text is truncated (full height is greater than clamped height)
                    // Add a small threshold (2px) to account for rounding/subpixel rendering
                    const isTruncated = fullHeight > clampedHeight + 2;
                    setShowToggle(isTruncated);
                }, 0);
                
                return () => clearTimeout(timeoutId);
            } else if (!qualification || qualification === "-") {
                setShowToggle(false);
            }
        }, [qualification, isExpanded]);

        if (!qualification || qualification === "-") {
            return <div>-</div>;
        }

        return (
            <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                <div
                    ref={textRef}
                    className={isExpanded ? 'opportunity-table-cell-text' : 'line-clamp-3 opportunity-table-cell-text'}
                    style={{
                       
                        overflowWrap: 'break-all',
                        maxWidth: '100%',
                        width: '100%',
                        textWrap: 'pretty',
                        boxSizing: 'border-box',
                        overflow: isExpanded ? 'visible' : 'hidden',
                        whiteSpace: isExpanded ? 'normal' : undefined
                    }}
                    title={qualification}
                >
                    {qualification}
                </div>
                {showToggle && (
                    <span
                        title={isExpanded ? 'See Less' : 'See More'}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="see-more cursor-pointer as-text-blue"
                        style={{ display: 'block', marginTop: '4px' }}
                    >
                        {isExpanded ? 'See Less...' : 'See More...'}
                    </span>
                )}
            </div>
        );
    };

    const columns = useMemo(() => [
        {
            name: "Name",
            selector: "name",
            sortable: true,
            grow: 2,
            cell: (row) => (<div className='table-name-column line-clamp-2 opportunity-table-cell-text' title={row.name}>{row.name || "-"}</div>)
        },
        {
            name: "Status",
            selector: "status",
            sortable: true,
            grow: 1.2,
            cell: (row) => (
                <div className='line-clamp-2'>
                    <span style={{ display: 'inline-block', width: '100%' }}>
                        {RenderOpportunityStatus(row.status ?? '-').html}
                    </span>
                </div>
            )
        },
        {
            name: "Date",
            selector: 'date',
            sortable: true,
            grow: 0,
            width: "80px",
            sortFunction: (a, b) => {
                const formats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'];
                const dateA = moment(a.dateCreated || a.date, formats);
                const dateB = moment(b.dateCreated || b.date, formats);
                return dateB.valueOf() - dateA.valueOf(); // Newest first
            },
            cell: (row) => (
                <div className="line-clamp-3 opportunity-table-cell-text" title={row.dateCreated || row.date}>
                    {formatDate(row.dateCreated || row.date)}
                </div>
            )
        },
        {
            name: "Qualification",
            selector: "qualification",
            sortable: true,
            grow: 2.5,
            cell: (row) => <QualificationCell qualification={row.qualification} />
        },
        {
            name: 'Brand',
            selector: 'subsidiary',
            sortable: true,
            grow: 1.2,
            cell: (row) => (
                <div className='line-clamp-3 d-flex align-items-start opportunity-table-cell-text'>
                    {renderBrandWithMapping(row.subsidiary)}
                </div>
            )
        },
        {
            name: "School",
            selector: 'school',
            sortable: true,
            grow: 2,
            sortFunction: (a, b) => {
                const aKey = resolveSchoolSlug(a.school).toLowerCase();
                const bKey = resolveSchoolSlug(b.school).toLowerCase();
                return aKey.localeCompare(bKey);
            },
            cell: (row) => (
                <div className="line-clamp-3 d-flex align-items-start opportunity-table-cell-text">
                    <span style={{ display: 'inline-block', width: '100%' }}>
                        {RenderSchool(resolveSchoolSlug(row.school) || "-")?.html}
                    </span>
                </div>
            )
        },
        {
            name: 'Intake',
            selector: 'intake',
            sortable: true,
            grow: 0,
            width: "40px",
            cell: (row) => {
                // Convert "2026 Intake 1" to "2026-1" format
                const formatIntake = (intake) => {
                    if (!intake) return "-";
                    const intakeStr = String(intake).trim();
                    // Match patterns like "2026 Intake 1" (case insensitive)
                    const match = intakeStr.match(/(\d{4})\s*Intake\s*(\d+)/i);
                    if (match) return `${match[1]}-${match[2]}`;
                    return intakeStr; // Return as-is if format doesn't match
                };
                return <div className="line-clamp-3 opportunity-table-cell-text" title={row.intake}>{formatIntake(row.intake)}</div>;
            }
        },
        {
            name: "Delivery Method",
            selector: "deliveryMethod",
            sortable: true,
            grow: 1.2,
            cell: (row) => (
                <div className='line-clamp-3 d-flex align-items-start opportunity-table-cell-text'>
                    <span style={{ display: 'inline-block', width: '100%' }}>
                        {RenderLearningMethodGradebook(row.deliverymethod ?? "-")?.html}
                    </span>
                </div>
            )
        },
        {
            name: "Campus",
            selector: "campus",
            sortable: true,
            grow: 0,
            width: "50px",
            cell: (row) => (
                <div className="line-clamp-3 opportunity-table-cell-text">
                    {renderCampusWithMapping(row.campus)}
                </div>
            )
        },
        {
            name: "Sales Rep",
            selector: "salesRep",
            sortable: true,
            grow: 0,
            width: "90px",
            cell: (row) => (<div className="line-clamp-3 opportunity-table-cell-text" title={row.salesrep}>{row.salesrep || "-"}</div>)
        }
    ], [schoolMapping]);

    const hasData = Array.isArray(opportunityData) && opportunityData.length > 0;
    const hasFilteredData = Array.isArray(dataFiltered) && dataFiltered.length > 0;
    const noMatchesForSearch = hasData && !hasFilteredData && search.trim() !== '';

    return (
        <div className='call-dashboard-table table-card opportunities-table'>
            <div className="table-header">
                <div className="table-title">
                    <i className="fal fa-users"></i> Opportunities
                </div>
                {hasData && (
                    <div className="dataTables_filter">
                        <label>
                            <input
                                type="search"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-controls="opportunities-table"
                            />
                        </label>
                        <div className="filter-eff filter-data-btn ml-2">
                            <button className="filter-buttons" type="button">
                                <i className="fal fa-filter"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="card-body p-2">
                {loading ? (
                    <div className="p-2"><SkeletonTicketList /></div>
                ) : error && !hasData ? (
                    <div className="empty-state error-state">
                        <i className="fal fa-exclamation-triangle text-danger mr-2"></i>
                        <span className="text-danger">{typeof error === 'string' ? error : 'Failed to load opportunities'}</span>
                        {onRetry && (
                            <button 
                                type="button"
                                className="btn btn-sm btn-outline-primary ml-3" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRetry();
                                }}
                            >
                                <i className="fal fa-redo mr-1"></i> Retry
                            </button>
                        )}
                    </div>
                ) : noMatchesForSearch ? (
                    <div className="empty-state">
                        No matches for "{search}"
                    </div>
                ) : hasFilteredData ? (
                    <div className="dataTables_filter">
                        <DataTableComponentFrontPagination
                            columns={columns}
                            isExport={false}
                            defaultSort={{'defaultSortColumn': 'date', 'defaultSortAsc': false}}
                            data={dataFiltered}
                            search={null} // We handle search externally to the component
                            setSearch={null}
                            
                            hideSearch="true"
                            custumcss="border-0"
                            otherTableProps={{
                                customStyles: {
                                    table: { 
                                        style: { 
                                            
                                            tableLayout: 'fixed'
                                        } 
                                    },
                                    headRow: { style: { height: '20px', minHeight: '20px', maxHeight: '20px', borderBottom: '1px solid #eee', padding: '0' } },
                                    rows: { style: { minHeight: '60px', border: "none" } }
                                },
                                fixedHeader: true
                            }}
                        />
                    </div>
                ) : (
                    <div className="empty-state">
                        There are no records to display
                    </div>
                )}
            </div>
        </div>
    );
};

export default OpportunityBlock;
